import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { c, rgbaOf, tone, tracked } from '../theme';
import { Notice, Wordmark } from '../components/Brand';
import { Menu } from '../components/Menu';
import { OfferPanel } from '../components/OfferPanel';
import { PhotoBand } from '../components/PhotoBand';
import { rowInfo } from '../rowInfo';
import { groupRows } from '../rowGroups';
import { scrubModule } from '../scrub';
import { ChartFor, ChartHideEmpty, ChartTextScale } from './document/chart';
import { api, type Dossier, type Module, type ReportState, type ReportSummary, type SignalRow, type User } from '../api';

/** `embedded`: drawn inside another page (the marketing site's phone), so it must not scroll the host window. */
export function ModuleDetail({ user, reports, embedded = false }: { user: User | null; reports: ReportSummary[]; embedded?: boolean }) {
  const { id = '', moduleKey = '' } = useParams();
  const [state, setState] = useState<ReportState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    api
      .report(id)
      .then((r) => live && setState(r))
      .catch((err) => live && setError(err instanceof Error ? err.message : 'Could not load the report.'));
    return () => {
      live = false;
    };
  }, [id]);

  useEffect(() => {
    if (!embedded) window.scrollTo(0, 0);
  }, [moduleKey, embedded]);

  const d = state?.dossier ?? null;
  const modules = d?.modules ?? [];
  const index = modules.findIndex((m) => m.key === moduleKey);
  const found = index >= 0 ? modules[index] : moduleKey === 'sources' && d ? sourcesModule(d) : null;
  const module = found ? scrubModule(found) : null;
  const assembling = reports.find((r) => r.status === 'assembling') ?? null;

  const goTo = (delta: number) => {
    if (modules.length === 0) return;
    const from = index >= 0 ? index : 0;
    const next = (from + delta + modules.length) % modules.length;
    navigate(`/report/${id}/${modules[next].key}`);
  };

  return (
    <Stage>
      {/* Header — back, wordmark, menu */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '48px var(--gx) 0' }}>
        <button
          onClick={() => navigate(`/report/${id}`, { state: { restore: true } })}
          aria-label="Back"
          style={{
            width: 36,
            height: 36,
            margin: '-6px 0 -6px -8px',
            borderRadius: '50%',
            border: `1px solid ${c.ink18}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: c.ink,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M8.5 3L4 7l4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <Wordmark />
        <Menu
          current="report"
          user={user}
          reportCount={reports.length}
          currentReport={state ? { id, address: state.address } : null}
          assembling={assembling ? { id: assembling.id, address: assembling.address } : null}
        />
      </div>

      {error && (
        <div style={{ padding: '34px var(--gx)' }}>
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {!module && !error && (
        <div style={{ padding: '40px', fontSize: 12, letterSpacing: '0.16em', color: c.ink5, fontWeight: 600 }}>
          LOADING…
        </div>
      )}

      {module && d && (
        <>
          {/* Title block */}
          <div style={{ padding: '34px var(--gx) 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 8, letterSpacing: '0.2em', color: c.ink5 }}>
              {`MODULE ${module.num} · ${d.address.line1} · ${d.address.locality} ${d.address.state}`.toUpperCase()}
            </div>
            <div style={{ fontSize: 26, lineHeight: 1.1, fontWeight: 700 }}>{module.title}</div>
            <div style={{ fontSize: 11, lineHeight: 1.55, color: c.ink6 }}>{module.intro}</div>
          </div>

          {/* Hero */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '30px var(--gx) 34px',
              gap: 14,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 10, color: c.red, fontWeight: 600, ...tracked(0.16) }}>
              {module.hero.label}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span
                style={{
                  fontSize: 44,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  lineHeight: 1,
                  // Any score out of 10 is coloured by range, as the Bottom Line is on the overview.
                  color:
                    (module.key === 'your-position' || module.hero.unit === '/10') && /^\d+(\.\d)?$/.test(module.hero.value)
                      ? Number(module.hero.value) >= 7
                        ? '#1E8A5A'
                        : Number(module.hero.value) >= 4
                          ? '#C9741A'
                          : c.red
                      : undefined,
                }}
              >
                {module.hero.value}
              </span>
              {module.hero.unit && <span style={{ fontSize: 12, color: c.ink5 }}>{module.hero.unit}</span>}
            </div>
            {module.hero.caption && (
              <div style={{ fontSize: 11, lineHeight: 1.55, color: c.ink6, maxWidth: 280 }}>{module.hero.caption}</div>
            )}
            <div
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: tone[module.hero.tone],
                ...tracked(0.14),
                border: `1px solid ${rgbaOf(tone[module.hero.tone].startsWith('#') ? tone[module.hero.tone] : '#1F1A1F', 0.45)}`,
                padding: '6px 12px',
                borderRadius: 999,
              }}
            >
              {module.hero.tag}
            </div>
          </div>

          {/* The Basics carries the Street View photo of the property */}
          {module.key === 'property-profile' && (
            <PhotoBand photoUrl={d.photoUrl} badge={null} captured={d.streetView?.captured} address={d.address.full} />
          )}

          {/* The offer window tab carries the full panel in place of the stat strip */}
          {module.key === 'offer-window' && d.offer && <OfferPanel offer={d.offer} />}

          {/* Stat strip */}
          {module.key !== 'offer-window' && module.stats.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(1, module.stats.length)}, 1fr)`,
              padding: '0 24px',
              borderTop: `1px solid ${c.hairline}`,
            }}
          >
            {module.stats.map((st, i) => (
              <div
                key={`${st.k}-${i}`}
                style={{
                  padding: '26px 10px 28px',
                  borderRight: i < module.stats.length - 1 ? `1px solid ${c.hairline}` : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 9, letterSpacing: '0.14em', color: c.red, fontWeight: 600 }}>{st.k}</div>
                <div style={{ fontSize: 18, lineHeight: 1, fontWeight: 600, letterSpacing: '-0.01em' }}>{st.v}</div>
                <div style={{ fontSize: 10, lineHeight: 1.4, color: c.ink5 }}>{st.s}</div>
              </div>
            ))}
          </div>

          )}

          {/* The same chart the PDF report draws for this module */}
          {/* The Bottom Line's four measures as raised bars, in the ring's colours */}
          {module.key === 'your-position' && (
            <RevealOnView key={module.key}>
              <MeasureBars rows={module.rows} />
            </RevealOnView>
          )}

          {module.key !== 'offer-window' && module.key !== 'your-position' && module.key !== 'sources' && (
            <RevealOnView key={module.key} className="rb-chart rb-chart-3d" style={{ padding: '26px 14px 12px' }}>
              <ChartHideEmpty.Provider value={true}>
                <ChartTextScale.Provider value={1.15}>
                  <ChartFor module={module} midpoint={d.hero.midpoint} width={360} />
                </ChartTextScale.Provider>
              </ChartHideEmpty.Provider>
            </RevealOnView>
          )}

          {/* Signals panel */}
          <div
            style={{
              background: c.creamDeep,
              borderTop: `1px solid ${c.hairline}`,
              padding: '44px 0 40px',
              display: 'flex',
              flexDirection: 'column',
              gap: 30,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '0 var(--gx)' }}>
              <div style={{ fontSize: 24, letterSpacing: '0.01em', whiteSpace: 'nowrap', fontWeight: 500 }}>
                {module.key === 'sources' ? (
                  'About this data'
                ) : (
                  <>
                    <span style={{ fontWeight: 700 }}>{module.rows.length}</span> signals
                  </>
                )}
              </div>
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: '0.16em',
                  fontWeight: 700,
                  color: c.ink5,
                  whiteSpace: 'nowrap',
                }}
              >
                {module.key === 'sources' ? (
                  ''
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    VERIFIED
                    <span aria-hidden style={{ display: 'inline-flex', gap: 3, flexWrap: 'wrap', maxWidth: 120 }}>
                      {module.rows.map((r, i) => (
                        <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: dotOf(r) }} />
                      ))}
                    </span>
                  </span>
                )}
              </div>
            </div>

            <SignalGroups moduleKey={module.key} rows={module.rows} />

            {module.note && <div style={{ fontSize: 11, lineHeight: 1.55, color: c.ink5, padding: '0 var(--gx)' }}>{module.note}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 6, paddingLeft: 'var(--gx)', paddingRight: 'var(--gx)' }}>
              <button
                onClick={() => navigate(`/report/${id}`)}
                style={{
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  fontWeight: 600,
                  border: `1px solid ${c.redLine}`,
                  padding: '10px 16px',
                  borderRadius: 999,
                  color: c.red,
                }}
              >
                BACK TO OVERVIEW
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <NavCircle label="Previous module" onClick={() => goTo(-1)} d="M8.5 3L4 7l4.5 4" />
                <NavCircle label="Next module" onClick={() => goTo(1)} d="M5.5 3L10 7l-4.5 4" />
              </div>
            </div>
          </div>
        </>
      )}
    </Stage>
  );
}

/**
 * Draws its chart in from left to right the first time it scrolls into view
 * (or straight away if it is already on screen when the page opens).
 */
function RevealOnView({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return setSeen(true);
    const io = new IntersectionObserver(
      (es) => {
        if (es.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`rb-reveal${seen ? ' rb-reveal-in' : ''}${className ? ` ${className}` : ''}`} style={style}>
      {children}
    </div>
  );
}

function NavCircle({ label, onClick, d }: { label: string; onClick: () => void; d: string }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: `1px solid ${c.ink25}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: c.ink,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
        <path d={d} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/** The flag button opens this: every call the report made, and what it cost. */
function sourcesModule(d: Dossier): Module {

  // Craig (#50): the score supports confidence; the list of every call does
  // not belong in front of a buyer. This page carries the score, then the
  // attribution and data-use statements.
  return {
    key: 'sources',
    num: '—',
    title: 'Sources & Reliability',
    sub: `Data reliability ${(d.reliability.score / 10).toFixed(1)}/10`,
    tag: `${(d.reliability.score / 10).toFixed(1)}/10`,
    tone: d.reliability.score >= 80 ? 'ok' : 'warn',
    intro: 'How complete the data behind this report is, where it comes from, and how it is used.',
    hero: {
      label: 'DATA RELIABILITY',
      value: (d.reliability.score / 10).toFixed(1),
      unit: '/10',
      caption: 'How much of the data this report asked for came back with a value. A gap is missing data at the source, not an error.',
      tag: d.reliability.score >= 80 ? 'HIGH' : d.reliability.score >= 60 ? 'GOOD' : 'PARTIAL',
      tone: d.reliability.score >= 80 ? 'ok' : 'warn',
    },
    stats: [],
    rows: [
      {
        k: 'Property and market data',
        s: 'Property, sales, listing, suburb and hazard data supplied by Microburbs Pty Ltd (microburbs.com.au), used under licence.',
        v: 'Microburbs',
        t: 'DATA PROVIDER',
        tone: 'ok',
      },
      {
        k: 'Census data',
        s: 'Source: Australian Bureau of Statistics, Census of Population and Housing 2021, licensed under CC BY 4.0.',
        v: 'ABS',
        t: 'CC BY 4.0',
        tone: 'ok',
      },
      ...(d.photoUrl
        ? [{ k: 'Street imagery', s: 'Street View imagery © Google.', v: 'Google', t: 'IMAGERY', tone: 'ok' as const }]
        : []),
      {
        k: 'How this data is used',
        s: 'Retrieved for this report only. It is kept with this report so the report can be shown and downloaded again, and is never shared, sold, or used for any other report or purpose.',
        v: 'This report',
        t: 'SINGLE USE',
        tone: 'ok',
      },
      {
        k: 'Accuracy',
        s: 'Figures are as published by the providers when the report was built. Figures ReadyBuy derives from them are labelled as derived or estimated. This report is general information, not financial advice or a valuation.',
        v: 'As published',
        t: 'NOTE',
        tone: 'neutral',
      },
    ],
    note: '',
  };
}

function Stage({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rb-stage"
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        background: c.page,
        padding: '40px 0 60px',
      }}
    >
      <div
        className="rb-shell"
        style={{
          width: 390,
          maxWidth: '100%',
          background: c.screen,
          color: c.ink,
          borderRadius: '40px 40px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * One signal row. Where the line item needs explaining, an ⓘ beside its label
 * opens a short note under it (comment #44); tapping again closes it.
 */
/** A signal's dot colour: its rating, or grey when there is no reading. Shared by the group header and each line. */
const dotOf = (r: SignalRow): string =>
  r.v === '—' || !r.tone ? 'rgba(31,26,31,0.25)' : tone[r.tone].startsWith('#') ? tone[r.tone] : '#1F1A1F';

function SignalLine({ r }: { r: SignalRow }) {
  const [open, setOpen] = useState(false);
  const info = rowInfo(r.k);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: 14,
        padding: '16px 0',
        borderTop: `1px solid ${c.hairlineList}`,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span aria-hidden style={{ flex: 'none', width: 7, height: 7, borderRadius: '50%', background: dotOf(r), marginTop: 6.5 }} />
          <span style={{ fontSize: 14, fontWeight: 500, wordBreak: 'break-word', lineHeight: '20px' }}>{r.k}</span>
          {info && (
            <button
              type="button"
              className="rb-info"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={`About ${r.k}`}
              style={{
                flex: 'none',
                width: 20,
                height: 20,
                marginTop: 0,
                borderRadius: '50%',
                background: open ? c.ink : c.violet,
                boxShadow: open ? 'none' : '0 0 0 3px rgba(106,43,217,0.18)',
                color: '#FFFFFF',
                fontSize: 12,
                fontWeight: 700,
                fontStyle: 'italic',
                fontFamily: 'Georgia, serif',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background .15s, box-shadow .15s',
              }}
            >
              i
            </button>
          )}
        </div>
        {r.s && <div style={{ fontSize: 11, lineHeight: 1.45, color: c.ink5, paddingLeft: 15 }}>{r.s}</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, maxWidth: 132 }}>
        <div style={{ fontSize: 14, fontWeight: 600, textAlign: 'right', color: r.v === '—' ? c.ink4 : c.ink, lineHeight: 1.35 }}>{r.v}</div>
        {r.t && (
          <div
            style={{
              fontSize: 8,
              letterSpacing: '0.12em',
              fontWeight: 600,
              color: r.tone ? tone[r.tone] : c.ink5,
              textAlign: 'right',
              lineHeight: 1.4,
            }}
          >
            {r.t}
          </div>
        )}
      </div>
      {open && info && (
        <div
          role="note"
          style={{
            gridColumn: '1 / -1',
            fontSize: 12,
            lineHeight: 1.55,
            color: c.ink75,
            background: 'rgba(106,43,217,0.07)',
            border: '1px solid rgba(106,43,217,0.2)',
            borderRadius: 14,
            padding: '12px 14px',
          }}
        >
          {info}
        </div>
      )}
    </div>
  );
}


/**
 * A module's line items in categories (comment #47), in the same soft tinted
 * cards as the overview's modules, all closed until tapped, with a dot
 * per line item in its colour. One category, or none, shows the rows flat.
 */
function SignalGroups({ moduleKey, rows }: { moduleKey: string; rows: SignalRow[] }) {
  const groups = groupRows(moduleKey, rows);
  // All categories start closed and open only on a tap.
  const [open, setOpen] = useState<Record<number, boolean>>({});
  if (groups.length === 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', padding: '0 var(--gx)' }}>
        {rows.map((r, i) => (
          <SignalLine key={`${r.k}-${i}`} r={r} />
        ))}
      </div>
    );
  }
  const allOpen = groups.every((_, i) => open[i]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Same violet hint pill as the overview's modules */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '0 var(--gx)', marginTop: -6 }}>
        <button
          type="button"
          onClick={() => setOpen(Object.fromEntries(groups.map((_, i) => [i, !allOpen])))}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '9px 16px 9px 12px',
            borderRadius: 999,
            background: 'rgba(106,43,217,0.1)',
            border: '1px solid rgba(106,43,217,0.35)',
            color: c.violet,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.violet, animation: 'throb 2.4s ease-in-out infinite' }} />
          {allOpen ? 'Tap to collapse all' : 'Tap a group to expand'}
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 24px' }}>
        {groups.map((g, i) => {
          const isOpen = !!open[i];
          return (
            <div key={g.title} style={{ borderRadius: 30, background: isOpen ? c.tintOpen : c.tint, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)', overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setOpen({ ...open, [i]: !isOpen })}
                aria-expanded={isOpen}
                style={{ width: '100%', display: 'grid', gridTemplateColumns: '30px 1fr auto', alignItems: 'center', gap: 14, padding: '16px 18px 16px 16px', color: c.ink, textAlign: 'left' }}
              >
                {/* The brand's three dots mark a group you can open; coloured dots are kept for ratings */}
                <span aria-hidden style={{ width: 30, height: 30, borderRadius: '50%', border: `1px solid ${c.ink18}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2.5 }}>
                  {[c.red, c.violet, c.cyan].map((d) => (
                    <span key={d} style={{ width: 4, height: 4, borderRadius: '50%', background: d }} />
                  ))}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>{g.title}</span>
                    <span style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 600, color: c.ink5, whiteSpace: 'nowrap' }}>
                      {g.rows.every((r) => r.v === '—' || r.v === '') ? 'NO DATA' : `${g.rows.length} ${g.rows.length === 1 ? 'SIGNAL' : 'SIGNALS'}`}
                    </span>
                  </span>
                  <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} aria-hidden>
                    {g.rows.map((r, j) => (
                      <span key={j} style={{ width: 7, height: 7, borderRadius: '50%', background: dotOf(r) }} />
                    ))}
                  </span>
                </span>
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '1px solid rgba(31,26,31,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: `rotate(${isOpen ? 180 : 0}deg)`,
                    transition: 'transform .2s',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path d="M3 5.5L7 9.5l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
              {isOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', padding: '0 18px 8px 18px' }}>
                  {g.rows.map((r, j) => (
                    <SignalLine key={`${r.k}-${j}`} r={r} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const RING = ['#D63A2E', '#E22A8C', '#6A2BD9', '#3A3BD6', '#2BB3E6'];

/** The four measures behind the Bottom Line, each a raised bar out of 100 — as the PDF draws them. */
function MeasureBars({ rows }: { rows: SignalRow[] }) {
  return (
    <div style={{ margin: '26px 16px 6px', padding: '18px 20px 20px', borderRadius: 26, background: c.tint, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>
      <div style={{ fontSize: 9, letterSpacing: '0.16em', fontWeight: 700, color: c.red }}>THE FOUR MEASURES</div>
      {rows.map((r, i) => {
        const n = /^\d+(\.\d)?$/.test(r.v) ? Number(r.v) : null;
        const col = RING[i % RING.length];
        return (
          <div key={r.k} style={{ display: 'grid', gridTemplateColumns: '84px 1fr 46px', alignItems: 'center', gap: 10, paddingTop: 14 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 8.5, letterSpacing: '0.12em', fontWeight: 700 }}>
              <span style={{ width: 8, height: 8, flex: 'none', borderRadius: '50%', background: `radial-gradient(circle at 35% 30%, #FFFFFF 0%, ${col} 45%, ${col} 100%)`, boxShadow: '0 1px 2px rgba(31,26,31,0.3)' }} />
              {r.k.toUpperCase()}
            </span>
            <span style={{ height: 14, borderRadius: 999, background: 'rgba(31,26,31,0.07)', boxShadow: 'inset 0 1px 2px rgba(31,26,31,0.12)' }}>
              <span
                style={{
                  position: 'relative',
                  display: 'block',
                  height: '100%',
                  width: `${(n ?? 0) * 10}%`,
                  minWidth: n ? 14 : 0,
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${col}, ${RING[(i + 1) % RING.length]})`,
                  boxShadow: '0 1px 0 rgba(0,0,0,0.25), 0 2px 0 rgba(0,0,0,0.18), 0 5px 8px rgba(31,26,31,0.22)',
                  overflow: 'hidden',
                }}
              >
                <span style={{ position: 'absolute', left: 4, right: 4, top: 1.5, height: 5, borderRadius: 999, background: 'linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0))' }} />
              </span>
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
              {n != null ? r.v : '—'}
              {n != null && <span style={{ fontSize: 8, color: c.ink5, fontWeight: 600 }}>/10</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}
